import React, { useEffect, useState, useContext } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import DataSourcesSection from '../../../components/settings/DataSourcesSection';
import DataSourceFormDialog, { FREE_PLAN_MAX_MEMORY_TIER_MB } from '../../../components/settings/DataSourceFormDialog';
import DataSourceViewDialog from '../../../components/settings/DataSourceViewDialog';
import DeleteConfirmationDialog from '../../../components/settings/DeleteConfirmationDialog';
import DataSourceService from '../../../services/DataSourceService';
import { DEFAULT_INGESTION_BATCH_SIZE } from '../../../components/settings/ingestionBatchOptions';
import SemanticModelProvisioningService from '../../../services/SemanticModelProvisioningService';
import CreditsEngineService from '../../../services/CreditsEngineService';
import { PageContainer } from '../components/AdminContentWrapper';
import { DataContext } from '../../../context/DataContext';
import { getCurrentUser } from '@aws-amplify/auth';

const SemanticConnections = () => {
  const { userData, accountId } = useContext(DataContext);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [dataSources, setDataSources] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingSource, setViewingSource] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentEditId, setCurrentEditId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    // Snowflake: par de claves (ver SnowflakeConnectionFields)
    warehouse: '',
    role: '',
    schema: '',
    keyFingerprint: '',
    snowflakeKeyMode: '',
    snowflakeKeyToken: '',
    privateKeyPem: '',
    privateKeyPassphrase: '',
    name: '',
    type: 'database',
    databaseType: 'postgresql',
    databaseTypeOriginal: 'postgresql',
    host: '',
    hostOriginal: '',
    port: '5432',
    portOriginal: '5432',
    secretName: '',
    database: '',
    databaseOriginal: '',
    databaseVersion: '',
    username: '',
    usernameOriginal: '',
    password: '',
    description: '',
    isPublic: false,
    useSsl: true,
    erpType: '',
    erpUrl: '',
    crmType: '',
    crmUrl: '',
    file: null,
    fileName: '',
    fileSize: 0,
    fileType: '',
    hasHeaders: true,
    // File upload state fields
    connectionType: '',
    uploadState: 'idle',
    uploadProgress: 0,
    errorMessage: null,
    csvDelimiter: ',',
    // Fase 3 (motor "Estándar", whisperbi-back-standar-ingestion) --
    // "standard" preseleccionado por default, ver el diseño.
    uploadEngine: 'standard',
    lambdaMemoryMb: 1024
  });
  // Fase 3 -- chequeo suave de plan de pago (solo UX: deshabilita Spark en
  // el selector si el tenant no es plan de pago). El control real es la
  // Fase 4 (autoritativo, en el backend) -- fail-open acá a propósito
  // (true por default) para no bloquear la subida si esta llamada falla.
  const [isPaidPlan, setIsPaidPlan] = useState(true);
  const [formErrors, setFormErrors] = useState({
    name: false,
    host: false,
    port: false,
    database: false,
    username: false,
    password: false
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [sourceToDelete, setSourceToDelete] = useState(null);
  const [connectingSourceIds, setConnectingSourceIds] = useState([]);
  
  // Estados para prueba de conexión en el formulario
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [testConnectionResult, setTestConnectionResult] = useState(null);

  // Get current user ID
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const user = await getCurrentUser();
        setCurrentUserId(user.userId);
      } catch (err) {
        console.error('Error getting current user:', err);
      }
    };
    fetchCurrentUser();
  }, []);

  // Fase 3 -- chequeo suave de plan de pago (ver comentario junto a
  // isPaidPlan más arriba). Mismo criterio ya usado en
  // OptimizedChatComponent.js: plan_monthly_price > 0 == plan de pago.
  useEffect(() => {
    if (!accountId) return;
    CreditsEngineService.getLimits({ accountId })
      .then((response) => {
        const price = Number(response?.account_policy?.plan_monthly_price);
        const paid = Number.isFinite(price) && price > 0;
        setIsPaidPlan(paid);
        // Fase 4 -- sin plan de pago: Estándar hasta 512 MB (el backend
        // lo fuerza igual; esto solo evita que la UI muestre algo que no
        // se va a respetar).
        if (!paid) {
          setFormData((prev) => ({
            ...prev,
            uploadEngine: 'standard',
            lambdaMemoryMb: Math.min(prev.lambdaMemoryMb || FREE_PLAN_MAX_MEMORY_TIER_MB, FREE_PLAN_MAX_MEMORY_TIER_MB),
          }));
        }
      })
      .catch((err) => {
        console.error('No se pudo verificar el plan de la cuenta:', err);
      });
  }, [accountId]);

  useEffect(() => {
    fetchDataSources();

    // Self-healing safety net: catch up any file (Excel/CSV) connection
    // whose semantic_database/semantic_table never got created because the
    // tab was closed/navigated away before the post-upload background poll
    // finished. Fire-and-forget — doesn't block the connections list.
    SemanticModelProvisioningService.reconcileFileConnections().catch((err) => {
      console.error('Error al reconciliar conexiones de archivo:', err);
    });
  }, []);

  // silent: recarga sin reemplazar la lista por el spinner (botón Actualizar).
  const fetchDataSources = async ({ silent = false } = {}) => {
    try {
      if (silent) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);
      const sources = await DataSourceService.fetchDataSources();
      setDataSources(sources);
      if (sources.length === 0) {
        console.log('No data sources found');
      }
    } catch (err) {
      console.error('Error loading data sources:', err);
      if (err.message && err.message.includes('ResourceNotFoundException')) {
        setError('No se pudo acceder a la tabla de fuentes de datos. La tabla no existe o no tiene acceso a ella.');
      } else {
        setError('No se pudieron cargar las fuentes de datos: ' + err.message);
      }
      if (process.env.NODE_ENV === 'development' && err.message && err.message.includes('ResourceNotFoundException')) {
        console.log('Usando datos de ejemplo en modo desarrollo');
        setDataSources([
          {
            id: 'example-1',
            type: 'SQL',
            name: 'Base de datos de ejemplo',
            description: 'Esta es una fuente de datos de ejemplo para desarrollo',
            url: 'ejemplo-secretname',
            lastSync: new Date().toLocaleString(),
            status: 'pending'
          }
        ]);
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  // Botón "Actualizar": lo mismo que recargar la página (lista + creación
  // del modelo semántico de las cargas que terminaron después de que el
  // polling en segundo plano se detuvo), sin perder la pantalla.
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await SemanticModelProvisioningService.reconcileFileConnections();
    } catch (err) {
      console.error('Error al reconciliar conexiones de archivo:', err);
    }
    await fetchDataSources({ silent: true });
  };

  const handleDialogOpen = async (isEdit = false, sourceData = null) => {
    setIsEditMode(isEdit);
    
    // Reset test connection result when opening dialog
    setTestConnectionResult(null);
    setIsTestingConnection(false);

    if (isEdit && sourceData) {
      try {
        const updatedSource = await DataSourceService.getDataSourceById(sourceData.id);
        sourceData = updatedSource || sourceData;
        const { id, name, type, url, description } = sourceData;
        setCurrentEditId(id);

        const formType = type === 'SQL' ? 'database' :
          type === 'ERP' ? 'erp' :
          type === 'CRM' ? 'crm' :
          type === 'Excel' ? 'file' : 'database';

        let formDataInit = {
          name: name || '',
          type: formType,
          host: '',
          secretName: url || '',
          database: sourceData.databaseName || '',
          databaseVersion: sourceData.databaseVersion || '',
          username: '',
          password: '',
          description: description || '',
          isPublic: sourceData.isPublic === true,
          useSsl: sourceData.useSsl !== false,
          databaseType: 'postgresql',
          databaseTypeOriginal: 'postgresql',
          hostOriginal: '',
          databaseOriginal: sourceData.databaseName || '',
          usernameOriginal: ''
        };

        // Conexión relacional: conservar el motor real (antes siempre se
        // reabría como PostgreSQL) y, en Snowflake, sus parámetros.
        const relationalType = (sourceData.connectionType || '').toLowerCase();
        if (formType === 'database' && relationalType) {
          formDataInit.databaseType = relationalType;
          formDataInit.databaseTypeOriginal = relationalType;
        }
        if (relationalType === 'snowflake') {
          formDataInit = {
            ...formDataInit,
            warehouse: sourceData.warehouse || '',
            role: sourceData.role || '',
            schema: sourceData.schema || '',
            keyFingerprint: sourceData.keyFingerprint || '',
            snowflakeKeyMode: 'keep',
            snowflakeKeyToken: '',
            privateKeyPem: '',
            privateKeyPassphrase: ''
          };
        }

        // File connections: pre-populate the current file's name/type so the
        // form can show "current file" and offer to replace it, and so a
        // rename-only save (no new file picked) knows there's nothing to upload.
        if (formType === 'file') {
          formDataInit = {
            ...formDataInit,
            // Cada reapertura arranca en "Reemplazar": la carga incremental
            // se elige explícitamente para cada archivo.
            loadMode: 'replace',
            file: null,
            fileName: sourceData.fileName || '',
            fileSize: 0,
            fileType: '',
            hasHeaders: true,
            connectionType: sourceData.connectionType || 'excel',
            uploadState: 'idle',
            uploadProgress: 0,
            errorMessage: null,
            csvDelimiter: ','
          };
        }

        // Note: Credentials are now stored encrypted in DynamoDB by the backend
        // We don't retrieve them for editing - user must re-enter if changing
        // The backend will preserve existing credentials if not provided in update

        setFormData(formDataInit);
      } catch (err) {
        console.error('Error loading source details:', err);
        setError('No se pudieron cargar los detalles de la fuente');
        return;
      }
    } else {
      setCurrentEditId(null);
      setFormData({
        // Snowflake: par de claves (ver SnowflakeConnectionFields)
        warehouse: '',
        role: '',
        schema: '',
        keyFingerprint: '',
        snowflakeKeyMode: '',
        snowflakeKeyToken: '',
        privateKeyPem: '',
        privateKeyPassphrase: '',
        name: '',
        type: 'database',
        databaseType: 'postgresql',
        databaseTypeOriginal: 'postgresql',
        host: '',
        hostOriginal: '',
        port: '5432',
        portOriginal: '5432',
        secretName: '',
        database: '',
        databaseOriginal: '',
        databaseVersion: '',
        username: '',
        usernameOriginal: '',
        password: '',
        description: '',
        isPublic: false,
        useSsl: true,
        erpType: '',
        erpUrl: '',
        crmType: '',
        crmUrl: '',
        file: null,
        fileName: '',
        fileSize: 0,
        fileType: '',
        hasHeaders: true,
        connectionType: '',
        uploadState: 'idle',
        uploadProgress: 0,
        errorMessage: null,
        csvDelimiter: ',',
        uploadEngine: 'standard',
        lambdaMemoryMb: isPaidPlan ? 1024 : FREE_PLAN_MAX_MEMORY_TIER_MB
      });
    }

    setFormErrors({
      name: false,
      host: false,
      port: false,
      database: false,
      username: false,
      password: false
    });

    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    if (isSaving) return;

    setDialogOpen(false);
    setIsEditMode(false);
    setCurrentEditId(null);
    setIsSaving(false);
    
    // Reset test connection result when closing dialog
    setTestConnectionResult(null);
    setIsTestingConnection(false);
  };

  const validateForm = () => {
    const errors = {
      name: !formData.name.trim()
    };

    if (formData.type === 'database' && formData.databaseType === 'snowflake') {
      // Snowflake: sin contraseña; warehouse/rol obligatorios y, al crear,
      // una clave (generada o subida). En edición, campos vacíos = sin cambio.
      const blank = (v) => !(v || '').trim();
      errors.host = !isEditMode && blank(formData.host);
      errors.database = !isEditMode && blank(formData.database);
      errors.username = !isEditMode && blank(formData.username);
      errors.warehouse = blank(formData.warehouse);
      errors.role = blank(formData.role);
      const mode = formData.snowflakeKeyMode || (isEditMode ? 'keep' : 'generate');
      errors.snowflakeKey = (mode === 'generate' && !formData.snowflakeKeyToken) ||
        (mode === 'upload' && blank(formData.privateKeyPem));
    } else if (formData.type === 'database') {
      errors.host = (!isEditMode && !formData.host.trim()) ||
        (isEditMode && formData.host.trim() !== formData.hostOriginal && !formData.host.trim());
      errors.database = (!isEditMode && !formData.database.trim()) ||
        (isEditMode && formData.database.trim() !== formData.databaseOriginal && !formData.database.trim());
      errors.username = (!isEditMode && !formData.username.trim()) ||
        (isEditMode && formData.username.trim() !== formData.usernameOriginal && !formData.username.trim());
      errors.password = !isEditMode && !formData.password.trim();
    } else if (formData.type === 'erp') {
      errors.erpType = !formData.erpType;
      errors.erpUrl = !formData.erpUrl;
      errors.username = !formData.username;
      errors.password = !isEditMode && !formData.password.trim();
    } else if (formData.type === 'crm') {
      errors.crmType = !formData.crmType;
      errors.crmUrl = !formData.crmUrl;
      errors.username = !formData.username;
      errors.password = !isEditMode && !formData.password.trim();
    } else if (formData.type === 'file') {
      errors.file = !isEditMode && !formData.file;
    }

    setFormErrors(errors);
    return !Object.values(errors).some((value) => value);
  };

  const handleSaveDataSource = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      // ── FILE CONNECTION FLOW ────────────────────────────────────────────────
      if (formData.type === 'file') {
        const file = formData.file;
        const connectionType = formData.connectionType || 'excel';

        setFormData(prev => ({ ...prev, uploadState: 'uploading', uploadProgress: 0, errorMessage: null }));

        let sourceConnectionId;

        if (isEditMode && currentEditId) {
          sourceConnectionId = currentEditId;

          // Sync name/description/visibility on the existing connection.
          // Uses a dedicated minimal update — see updateFileConnectionMetadata
          // for why the generic updateDataSource path can't be reused here.
          await DataSourceService.updateFileConnectionMetadata(sourceConnectionId, {
            name: formData.name,
            description: formData.description || '',
            isPublic: formData.isPublic === true,
            // Solo si se eligió un archivo nuevo: reemplazar o carga incremental.
            loadMode: file ? (formData.loadMode || 'replace') : undefined,
            ingestionBatchSize: file ? (formData.ingestionBatchSize || DEFAULT_INGESTION_BATCH_SIZE) : undefined,
          });

          if (!file) {
            // Rename/description-only edit — no new file to (re)ingest.
            await fetchDataSources();
            handleDialogClose();
            return;
          }
          // A new file WAS picked: fall through and re-run the upload/ingest
          // pipeline against the SAME connection id. file-ingestion overwrites
          // the existing Glue table + Parquet object for this connection_id,
          // and SemanticModelProvisioningService is idempotent by connection_id
          // (updates the existing semantic_table instead of duplicating it).
        } else {
          const createdConnection = await DataSourceService.createFileConnection({
            name: formData.name,
            connectionType,
            description: formData.description || '',
            isPublic: formData.isPublic === true,
            csvDelimiter: formData.csvDelimiter,
            uploadEngine: formData.uploadEngine,
            lambdaMemoryMb: formData.lambdaMemoryMb,
            ingestionBatchSize: formData.ingestionBatchSize || DEFAULT_INGESTION_BATCH_SIZE,
          });
          sourceConnectionId = createdConnection.source_connection_id;
        }

        // Step 2: Get presigned upload URL
        const contentType = connectionType === 'csv'
          ? 'text/csv'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

        const uploadData = await DataSourceService.getFileUploadUrl(
          sourceConnectionId,
          file.name,
          contentType
        );

        // Step 3: Upload file to S3 via XHR for progress tracking
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', uploadData.upload_url);
          xhr.setRequestHeader('Content-Type', contentType);
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              const pct = Math.round((e.loaded / e.total) * 100);
              setFormData(prev => ({ ...prev, uploadProgress: pct }));
            }
          };
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else reject(new Error(`S3 upload failed: ${xhr.status}`));
          };
          xhr.onerror = () => reject(new Error('S3 upload network error'));
          xhr.send(file);
        });

        // Step 4: Confirm upload
        setFormData(prev => ({ ...prev, uploadState: 'confirming', uploadProgress: 100 }));
        await DataSourceService.confirmFileUpload(sourceConnectionId, uploadData.s3_key);

        // Step 5: Close dialog immediately — ingestion runs async in the background.
        // Refresh the list so the new connection shows with status 'processing'.
        await fetchDataSources();
        handleDialogClose();

        // Optionally poll in the background and refresh the list when done,
        // without blocking the UI or throwing on transient errors.
        const POLL_INTERVAL_MS = 5000;
        const TIMEOUT_MS = 5 * 60 * 1000;
        const startTime = Date.now();
        const pollInBackground = async () => {
          if (Date.now() - startTime > TIMEOUT_MS) return;
          try {
            // Ingesta multi-hoja: se llama getRawSourceConnection directo en
            // vez de pollConnectionStatus (que internamente hace exactamente
            // la misma llamada y solo devuelve connection_status) para tener,
            // en el mismo request, el detalle por hoja (sheet_progress,
            // total_sheets, etc.) y poder mostrarlo en la tarjeta de la
            // conexión mientras se procesa, sin bloquear el diálogo.
            const rawConnection = await DataSourceService.getRawSourceConnection(sourceConnectionId);
            const status = rawConnection.connection_status;

            if (status === 'active' || status === 'ready') {
              // La ingesta terminó: file-ingestion dejó los payloads de
              // semantic_database/semantic_table listos en la conexión, pero
              // no los pudo persistir (no tiene sesión de Cognito). Se crean
              // aquí con la sesión real del usuario logueado.
              try {
                const provisionResult = await SemanticModelProvisioningService.provisionFromConnection(rawConnection);
                if (!provisionResult.success) {
                  console.error('Error al crear el modelo semántico para la conexión:', provisionResult.error);
                }
              } catch (provisionError) {
                console.error('Error al aprovisionar el modelo semántico:', provisionError);
              }
              fetchDataSources(); // refresh list with final status
              return;
            }
            if (status === 'error') {
              fetchDataSources(); // refresh list with final status
              return;
            }

            // Todavía procesando -- actualiza el progreso por hoja de ESTA
            // tarjeta en memoria (sin refrescar el resto de la lista) para
            // que se vea "Procesando X/Y hojas..." mientras el diálogo ya
            // está cerrado y el usuario sigue usando la pantalla.
            setDataSources((prev) => prev.map((ds) => (
              ds.id === sourceConnectionId
                ? {
                    ...ds,
                    totalSheets: rawConnection.total_sheets ?? ds.totalSheets,
                    sheetsCompleted: rawConnection.sheets_completed ?? ds.sheetsCompleted,
                    sheetsFailed: rawConnection.sheets_failed ?? ds.sheetsFailed
                  }
                : ds
            )));
          } catch (_e) {
            // ignore transient errors, keep polling
          }
          setTimeout(pollInBackground, POLL_INTERVAL_MS);
        };
        setTimeout(pollInBackground, POLL_INTERVAL_MS);
        return;
      }

      // ── RELATIONAL / ERP / CRM CONNECTION FLOW ──────────────────────────────
      let updatedFormData = {
        ...formData,
        connectionStatus: 'pending'
      };

      console.log('Datos a guardar en la API:', updatedFormData);

      if (isEditMode) {
        const updatedSource = await DataSourceService.updateDataSource(currentEditId, updatedFormData);
        updatedSource.status = 'pending';
        setDataSources((prevSources) =>
          prevSources.map((source) =>
            source.id === currentEditId ? updatedSource : source
          )
        );
      } else {
        const newSource = await DataSourceService.createDataSource(updatedFormData);
        setDataSources((prev) => [...prev, newSource]);
      }

      handleDialogClose();
    } catch (err) {
      console.error('Error saving data source:', err);
      // For file uploads, show error inline in the dialog without closing it
      if (formData.type === 'file') {
        setFormData(prev => ({
          ...prev,
          uploadState: 'error',
          errorMessage: err.message || 'Error desconocido al subir el archivo'
        }));
        setIsSaving(false);
        return;
      }
      setError('Error al guardar la fuente de datos: ' + (err.message || 'Error desconocido'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.match(/\.(xlsx|xls|csv)$/i)) {
        setFormErrors({
          ...formErrors,
          file: 'Solo se aceptan archivos .xlsx, .xls o .csv'
        });
        return;
      }

      if (file.size > 52428800) {
        setFormErrors({
          ...formErrors,
          file: 'El archivo no puede superar los 50 MB'
        });
        return;
      }

      const newErrors = { ...formErrors };
      delete newErrors.file;
      setFormErrors(newErrors);

      // Derive connectionType from extension
      const ext = file.name.split('.').pop().toLowerCase();
      const connectionType = ext === 'csv' ? 'csv' : 'excel';

      setFormData({
        ...formData,
        file,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        connectionType,
        // Reset any previous upload state when a new file is selected
        uploadState: 'idle',
        uploadProgress: 0,
        errorMessage: null,
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Reset test connection result when user changes connection parameters
    if (['host', 'port', 'database', 'username', 'password', 'databaseType', 'useSsl',
      'warehouse', 'role', 'schema', 'snowflakeKeyToken', 'privateKeyPem', 'privateKeyPassphrase'].includes(name)) {
      setTestConnectionResult(null);
    }

    if (name === 'isPublic' || name === 'useSsl' || name === 'hasHeaders') {
      processedValue = value === 'true' || value === true;
    }

    if (name === 'type') {
      // Reset test connection result when changing type
      setTestConnectionResult(null);
      
      const baseFormData = {
        ...formData,
        [name]: processedValue
      };

      if (processedValue === 'database') {
        setFormData({
          ...baseFormData,
          databaseType: 'postgresql',
          host: '',
          port: '5432',
          database: '',
          databaseVersion: '',
          username: '',
          password: '',
          erpType: '',
          erpUrl: '',
          crmType: '',
          crmUrl: '',
          file: null,
          fileName: '',
          fileSize: 0,
          fileType: '',
          hasHeaders: true
        });
      } else if (processedValue === 'erp') {
        setFormData({
          ...baseFormData,
          erpType: '',
          erpUrl: '',
          username: '',
          password: '',
          crmType: '',
          crmUrl: '',
          file: null,
          fileName: '',
          fileSize: 0,
          fileType: '',
          hasHeaders: true
        });
      } else if (processedValue === 'crm') {
        setFormData({
          ...baseFormData,
          crmType: '',
          crmUrl: '',
          username: '',
          password: '',
          erpType: '',
          erpUrl: '',
          file: null,
          fileName: '',
          fileSize: 0,
          fileType: '',
          hasHeaders: true
        });
      } else if (processedValue === 'file') {
        setFormData({
          ...baseFormData,
          file: null,
          fileName: '',
          fileSize: 0,
          fileType: '',
          hasHeaders: true,
          databaseType: '',
          host: '',
          database: '',
          databaseVersion: '',
          username: '',
          password: '',
          erpType: '',
          erpUrl: '',
          crmType: '',
          crmUrl: '',
          connectionType: '',
          uploadState: 'idle',
          uploadProgress: 0,
          errorMessage: null,
          csvDelimiter: ','
        });
      }

      setFormErrors({
        name: formErrors.name
      });

      return;
    }

    // Cambiar puerto automáticamente cuando se cambia el tipo de base de datos
    if (name === 'databaseType') {
      const defaultPorts = {
        'postgresql': '5432',
        'mysql': '3306',
        'sqlserver': '1433',
        'oracle': '1521',
        'redshift': '5439',
        'snowflake': '443',
        'bigquery': '443'
      };

      setFormData({
        ...formData,
        [name]: processedValue,
        port: defaultPorts[processedValue] || '5432'
      });

      if (formErrors[name]) {
        setFormErrors({
          ...formErrors,
          [name]: false
        });
      }

      return;
    }

    // Forma funcional: SnowflakeConnectionFields encadena varios cambios
    // seguidos y con `...formData` solo sobreviviría el último.
    setFormData((prev) => ({
      ...prev,
      [name]: processedValue
    }));

    if (formErrors[name] || (formErrors.snowflakeKey && ['snowflakeKeyToken', 'privateKeyPem'].includes(name))) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: false,
        snowflakeKey: ['snowflakeKeyToken', 'privateKeyPem'].includes(name) ? false : prev.snowflakeKey
      }));
    }
  };

  const handleTestConnection = async (id) => {
    setConnectingSourceIds((prev) => [...prev, id]);

    try {
      const result = await DataSourceService.testConnection(id);
      const isSuccessful = result.success;

      if (isSuccessful) {
        const updatedSource = await DataSourceService.getDataSourceById(id);
        setDataSources((prevSources) =>
          prevSources.map((source) =>
            source.id === id ? updatedSource : source
          )
        );
      } else {
        setDataSources((prevSources) =>
          prevSources.map((source) =>
            source.id === id
              ? { ...source, status: 'error' }
              : source
          )
        );
      }
    } catch (err) {
      console.error('Error testing connection:', err);
      setDataSources((prevSources) =>
        prevSources.map((source) =>
          source.id === id
            ? { ...source, status: 'error', lastSync: new Date().toLocaleString() }
            : source
        )
      );
    } finally {
      setConnectingSourceIds((prev) => prev.filter((sourceId) => sourceId !== id));
    }
  };

  // Función para probar la conexión desde el formulario (antes de guardar)
  const handleTestConnectionFromForm = async () => {
    console.log('Testing connection from form with data:', formData);

    if (formData.databaseType === 'snowflake') {
      const mode = formData.snowflakeKeyMode || (isEditMode ? 'keep' : 'generate');
      setIsTestingConnection(true);
      setTestConnectionResult(null);
      try {
        let result;
        if (isEditMode && mode === 'keep') {
          // Clave guardada: prueba la conexión tal como está.
          result = await DataSourceService.testConnection(currentEditId);
        } else if (isEditMode && mode === 'generate' && formData.snowflakeKeyToken) {
          // Rotación: prueba la clave nueva sin guardarla.
          result = await DataSourceService.testSnowflakeCandidateKey(currentEditId, formData.snowflakeKeyToken);
        } else {
          if (!formData.host || !formData.username || !formData.database || !formData.warehouse || !formData.role) {
            setTestConnectionResult({ success: false, message: 'Completa cuenta, usuario, base de datos, warehouse y rol' });
            return;
          }
          if (!formData.snowflakeKeyToken && !formData.privateKeyPem) {
            setTestConnectionResult({ success: false, message: 'Genera un par de claves o sube tu clave privada' });
            return;
          }
          result = await DataSourceService.testConnectionParameters({
            connection_type: 'snowflake',
            host: formData.host,
            database_name: formData.database,
            username: formData.username,
            warehouse: formData.warehouse,
            role: formData.role,
            schema: formData.schema || undefined,
            generated_key_token: formData.snowflakeKeyToken || undefined,
            private_key_pem: formData.snowflakeKeyToken ? undefined : formData.privateKeyPem,
            private_key_passphrase: formData.snowflakeKeyToken ? undefined : (formData.privateKeyPassphrase || undefined)
          });
        }
        setTestConnectionResult({
          success: result.success,
          message: result.message || (result.success ? 'Conexión exitosa' : 'Error en la conexión')
        });
      } finally {
        setIsTestingConnection(false);
      }
      return;
    }
    
    // Validar que tengamos los datos mínimos necesarios
    if (!formData.host || !formData.username || !formData.database) {
      setTestConnectionResult({
        success: false,
        message: 'Por favor complete todos los campos requeridos'
      });
      return;
    }
    
    // Si estamos en modo edición y no hay contraseña, no podemos probar
    if (isEditMode && !formData.password) {
      setTestConnectionResult({
        success: false,
        message: 'Ingrese la contraseña para probar la conexión'
      });
      return;
    }
    
    setIsTestingConnection(true);
    setTestConnectionResult(null);
    
    try {
      // Preparar los parámetros de conexión para la prueba
      const testParams = {
        connection_type: formData.databaseType || 'mysql',
        host: formData.host,
        port: formData.port || getDefaultPort(formData.databaseType),
        database_name: formData.database,
        username: formData.username,
        password: formData.password,
        use_ssl: formData.useSsl !== false
      };
      
      console.log('Testing connection with params:', { ...testParams, password: '***' });
      
      // Llamar a la API para probar la conexión
      const result = await DataSourceService.testConnectionParameters(testParams);
      
      console.log('Connection test result:', result);
      
      setTestConnectionResult({
        success: result.success,
        message: result.message || (result.success ? 'Conexión exitosa' : 'Error en la conexión')
      });
      
    } catch (err) {
      console.error('Error testing connection from form:', err);
      setTestConnectionResult({
        success: false,
        message: err.message || 'Error al probar la conexión'
      });
    } finally {
      setIsTestingConnection(false);
    }
  };
  
  // Función auxiliar para obtener el puerto por defecto según el tipo de base de datos
  const getDefaultPort = (databaseType) => {
    const defaultPorts = {
      'postgresql': 5432,
      'mysql': 3306,
      'sqlserver': 1433,
      'oracle': 1521,
      'redshift': 5439,
      'snowflake': 443,
      'bigquery': 443
    };
    return defaultPorts[databaseType] || 3306;
  };

  const handleEditSource = (id) => {
    const sourceToEdit = dataSources.find((source) => source.id === id);
    if (sourceToEdit) {
      handleDialogOpen(true, sourceToEdit);
    }
  };

  const handleViewSource = (id) => {
    const sourceToView = dataSources.find((source) => source.id === id);
    if (sourceToView) {
      setViewingSource(sourceToView);
      setViewDialogOpen(true);
    }
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setViewingSource(null);
  };

  const handleDeleteSource = (id) => {
    const sourceData = dataSources.find((source) => source.id === id);
    setSourceToDelete(sourceData);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (sourceToDelete) {
      try {
        // Para conexiones de archivo, limpiar primero el semantic_database/
        // semantic_table asociados (la conexión en sí, junto con la tabla de
        // Glue y los objetos en S3, se limpia en el backend al eliminar la
        // conexión). Best-effort: si falla, se avisa pero no bloquea el
        // borrado de la conexión.
        if (sourceToDelete.type === 'Excel' && sourceToDelete.account_id) {
          const deprovisionResult = await SemanticModelProvisioningService.deprovisionForConnection(
            sourceToDelete.id,
            sourceToDelete.account_id
          );
          if (!deprovisionResult.success) {
            console.error(
              'La conexión se eliminará, pero no se pudo limpiar por completo la entidad semántica asociada:',
              deprovisionResult.error
            );
          }
        }

        await DataSourceService.deleteDataSource(sourceToDelete.id);
        setDataSources((prev) => prev.filter((source) => source.id !== sourceToDelete.id));
        setDeleteConfirmOpen(false);
        setSourceToDelete(null);
      } catch (err) {
        console.error('Error deleting data source:', err);
        setError('No se pudo eliminar la fuente de datos: ' + (err.message || 'Error desconocido'));
      }
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setSourceToDelete(null);
  };

  return (
    <PageContainer
      title="Conexiones"
      description="Gestiona y monitorea las conexiones con tus fuentes operativas y analíticas."
      headerSpacing={2}
      actions={[
        <Button
          key="refresh"
          variant="outlined"
          sx={{ textTransform: 'none', fontWeight: 600 }}
          startIcon={isRefreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          disabled={isLoading || isRefreshing}
          onClick={handleRefresh}
        >
          {isRefreshing ? 'Actualizando…' : 'Actualizar'}
        </Button>,
        <Button
          key="new"
          variant="contained"
          color="primary"
          sx={{ textTransform: 'none', fontWeight: 600 }}
          onClick={() => handleDialogOpen(false)}
        >
          Nueva conexión
        </Button>
      ]}
    >
      <Box>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 6
            }}
          >
            <CircularProgress size={40} sx={{ color: 'var(--wb-primary)' }} />
          </Box>
        ) : error ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              bgcolor: 'var(--wb-x-bg-f4433614, rgba(244, 67, 54, 0.08))',
              borderRadius: 2,
              border: '1px solid var(--wb-x-border-f4433666, rgba(244, 67, 54, 0.4))',
              p: 3
            }}
          >
            <ErrorOutlineIcon sx={{ color: 'var(--wb-danger-strong)' }} />
            <Typography sx={{ color: 'var(--wb-danger-strong)' }}>{error}</Typography>
          </Box>
        ) : (
          <DataSourcesSection
            dataSources={dataSources}
            connectingSourceIds={connectingSourceIds}
            currentUserId={currentUserId}
            onAddClick={() => handleDialogOpen(false)}
            onEditClick={handleEditSource}
            onDeleteClick={handleDeleteSource}
            onViewClick={handleViewSource}
            onTestConnection={handleTestConnection}
            showHeader
          />
        )}
      </Box>

      <DataSourceFormDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSave={handleSaveDataSource}
        isEditMode={isEditMode}
        formData={formData}
        formErrors={formErrors}
        handleInputChange={handleInputChange}
        handleFileChange={handleFileChange}
        isSaving={isSaving}
        onTestConnection={handleTestConnectionFromForm}
        connectionId={currentEditId}
        isTestingConnection={isTestingConnection}
        testConnectionResult={testConnectionResult}
        isPaidPlan={isPaidPlan}
      />

      <DataSourceViewDialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        source={viewingSource}
      />

      <DeleteConfirmationDialog
        open={deleteConfirmOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        sourceToDelete={sourceToDelete}
      />
    </PageContainer>
  );
};

export default SemanticConnections;
